import {Component} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {TransactionalNames} from '../enums/TransactionalNames.js';
import {ads, UserGesture} from '@wonderlandengine/uber-sdk';
import {Statistics} from './statistics.js';
import {WLUtils} from '../utils/WLUtils.js';
import {AdAction, AdError, AdType} from '../enums/game-analytics-enums.js';

import type {GameAnalytics as GA} from 'gameanalytics';
import {MusicDirector} from './music-director.js';
import {ConfirmationDialogController} from '../ui/panels/confirmation-dialog.js';
import {OfflineManager} from '../classes/OfflineManager.js';
declare var GameAnalytics: typeof GA;

export enum AdRewardTypes {
    CoinReward, // Get some coins for watching an ad
    DoubleCoins, // immidiate double coins got for playing a game
    CoconutReward, // get a free coconut bomb
    TimedReward, // e.g. 1 hour of double coins
    RemoveCooldown, // Remove cooldown on a powerup
    PrePlay, // Ad shown to start playing a game. (not really a reward)
}

function trackAdError(e, adType: AdType, adPlacement: string) {
    let adError: AdError | null = AdError.Unknown;
    switch (e.status) {
        case 'unfilled':
        case 'ads-unavailable':
            adError = AdError.NoFill;
            break;
        case 'network-error':
            adError = AdError.Offline;
            break;
        case 'no-zoneid':
            adError = AdError.InvalidRequest;
            break;
        case 'ad-maximum':
        case 'ad-blocker':
        case 'ad-violation':
        case 'cors-error':
        case 'Unknown message': // TK: I've seen this coming from the ad provider. Not sure what it means.
            break;
        default:
            adError = null;
        /* Not an error, user might just have canceled */
    }

    if (adError !== null) {
        GameAnalytics.addAdEventWithNoAdReason(
            AdAction.FailedShow,
            adType,
            e.provider.name,
            adPlacement,
            adError
        );
    }
}

export class AdReward extends Component {
    static TypeName = 'ad-reward';

    @property.enum(Object.keys(AdRewardTypes).filter((e) => isNaN(Number(e))))
    rewardType: AdRewardTypes;

    @property.enum(Object.keys(TransactionalNames).filter((e) => isNaN(Number(e))))
    transaction: TransactionalNames;

    @property.int(0)
    amount = 0;

    @property.float(0)
    duration = 0;
}

/**
 * AdManager Component
 *
 * Based on the original purchase manager. This component was not in the original game,
 * but since ads will be a big part of the economy in the the game I created this component
 * with a lot of inspiration from the purchase manager.
 */
export class AdManager extends Component {
    static TypeName = 'ad-manager';

    // Singleton
    private static _instance: AdManager;
    static get instance(): AdManager {
        return AdManager._instance;
    }

    private _adRewards: Map<AdRewardTypes, AdReward> = new Map();

    init() {
        if (AdManager._instance) {
            console.error('There can only be one instance of AdManager Component');
        }
        AdManager._instance = this;
    }

    start() {
        WLUtils.getComponentsOfTypeRecursive(this.object, AdReward).forEach((adReward) => {
            this._adRewards.set(adReward.rewardType, adReward);
        });
    }

    showMidgameAd(userGesture: UserGesture): Promise<void> {
        if (OfflineManager.instance.isOffline) {
            return Promise.reject(new Error('Offline mode: Ads are disabled.'));
        }
        if (ads.hasAd() && userGesture) {
            const start = Date.now();
            const adPlacement = AdRewardTypes[AdRewardTypes.PrePlay];
            MusicDirector.instance.pause();
            return new Promise((resolve, reject) => {
                try {
                    ads.showMidgameAd(userGesture)
                        .then((provider) => {
                            const elapsedTime = Date.now() - start;
                            GameAnalytics.addAdEventWithDuration(
                                AdAction.Show,
                                AdType.Interstitial,
                                provider.name,
                                adPlacement,
                                elapsedTime
                            );
                            MusicDirector.instance.resume();
                            resolve();
                        })
                        .catch((e) => {
                            console.log('Ad failed:', e.status);
                            trackAdError(e, AdType.Interstitial, adPlacement);
                            MusicDirector.instance.resume();
                            reject();
                        });
                } catch (e) {
                    console.error('Error showing ad:', e);
                    MusicDirector.instance.resume();
                    reject(e);
                }
            });
        }
        return Promise.reject(new Error('No ad available'));
    }

    getAdReward(
        type: AdRewardTypes,
        userGesture: UserGesture,
        coins: number = undefined
    ): Promise<{type: AdRewardTypes; coins: number}> {
        if (OfflineManager.instance.isOffline) {
            return Promise.reject(new Error('Offline mode: Ads are disabled.'));
        }

        // Do some checks first: (Random thoughts TK)
        // - Is the user allowed to view the ad?
        // - Is there a cooldown timer on that ad?

        // Other question:
        // - Do we want to keep statistics on how many ads the user has seen?

        const adReward = this._adRewards.get(type);

        if (ads.hasAd() && userGesture) {
            const start = Date.now();
            const adPlacement = AdRewardTypes[type];
            MusicDirector.instance.pause();
            return new Promise((resolve, reject) => {
                try {
                    ads.showRewardedAd(userGesture)
                        .then((provider) => {
                            const elapsedTime = Date.now() - start;
                            GameAnalytics.addAdEventWithDuration(
                                AdAction.Show,
                                AdType.RewardedVideo,
                                provider.name,
                                adPlacement,
                                elapsedTime
                            );

                            GameAnalytics.addAdEvent(
                                AdAction.RewardReceived,
                                AdType.RewardedVideo,
                                provider.name,
                                adPlacement
                            );
                            switch (type) {
                                case AdRewardTypes.PrePlay:
                                    break;
                                default:
                                    if (!coins) {
                                        if (adReward.amount !== 0) {
                                            coins = adReward.amount;
                                            // reward the player
                                            Statistics.instance.addAnonymousPurchaseTransaction(
                                                adReward.transaction,
                                                adReward.amount
                                            );
                                        }
                                    } else {
                                        Statistics.instance.addAnonymousPurchaseTransaction(
                                            TransactionalNames.CoinCount,
                                            coins
                                        );
                                    }
                                    break;
                            }

                            MusicDirector.instance.resume();
                            resolve({type, coins});
                        })
                        .catch((e) => {
                            console.log('Ad failed:', e.status);
                            if (e.status === 'ad-blocked') {
                                ConfirmationDialogController.instance.displayDialog(
                                    'Oh no! Ads are blocked',
                                    'OK!'
                                );
                            }
                            trackAdError(e, AdType.RewardedVideo, adPlacement);
                            reject();
                        });
                } catch (e) {
                    console.error('Error showing ad:', e);
                    MusicDirector.instance.resume();
                    reject(e);
                }
            });
        }

        return Promise.reject(new Error('No ad available'));
    }

    isAdAvailable(adType: AdRewardTypes): boolean {
        return ads.hasAd(); // This is false if the user has no ads for some reason (blocked, limit reached, not ready).
    }
}
